import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatExpiry = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const SyncSpendBox = ({ clientId }) => {
  const today = new Date();
  const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const sync = useCallback(async (silent = false) => {
    if (!silent) {
      setBusy(true);
      setResult(null);
      setError(null);
    }
    try {
      const res = await axios.post(`${API}/facebook/sync-spend/${clientId}/${month}`);
      setResult(res.data?.data || res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Sync failed");
    } finally {
      if (!silent) setBusy(false);
    }
  }, [clientId, month]);

  return (
    <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded space-y-2" data-testid="fb-sync-spend-box">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs text-[#71717A]">Sync ad spend for month</label>
        <input
          type="month"
          className="input-default text-xs py-1 px-2 w-36"
          value={month}
          max={defaultMonth}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="fb-sync-spend-month"
        />
      </div>
      <button
        onClick={() => sync(false)}
        disabled={busy}
        className="w-full py-1.5 rounded font-medium text-xs transition-colors"
        style={{ background: busy ? "#52525B" : "#1877F2", color: "#fff" }}
        data-testid="fb-sync-spend-btn"
      >
        {busy ? "Syncing from Facebook…" : `Sync ${month} ad spend now`}
      </button>
      {result && (
        <div className="text-xs space-y-1 mt-2 p-2 rounded" style={{ background: "#22C55E15", color: "#86EFAC" }}>
          <div>✓ Total Spend: <span className="font-mono text-white">{result.total_spend?.toLocaleString?.("en-IN") || result.total_spend}</span> {result.currency || ""}</div>
          {typeof result.total_impressions === "number" && (
            <div>Impressions: {result.total_impressions.toLocaleString("en-IN")} · Clicks: {result.total_clicks?.toLocaleString?.("en-IN") || 0}</div>
          )}
          <div className="text-[10px] text-[#71717A]">Saved to Ad Spend for {month}. Refresh the billing panel to use.</div>
        </div>
      )}
      {error && (
        <div className="text-xs p-2 rounded" style={{ background: "#FF444422", color: "#FF6666" }}>{error}</div>
      )}
    </div>
  );
};

const ClientFacebookConnection = ({ clientId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [error, setError] = useState(null);
  const [savedToast, setSavedToast] = useState(null);
  const [previewInfo, setPreviewInfo] = useState(null); // { redirect_uri, auth_url, resolved_via }
  const [showTroubleshoot, setShowTroubleshoot] = useState(false);

  const copyToClipboard = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      setSavedToast(label);
      setTimeout(() => setSavedToast(null), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setSavedToast(label);
      setTimeout(() => setSavedToast(null), 2000);
    }
  };

  const previewAuthUrl = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/facebook/auth-url`, {
        params: { origin: window.location.origin, preview: 1 },
      });
      setPreviewInfo({
        redirect_uri: res.data.redirect_uri,
        auth_url: res.data.auth_url,
        resolved_via: res.data.resolved_via,
      });
      setShowTroubleshoot(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not generate preview");
    } finally {
      setLoading(false);
    }
  };

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.get(`${API}/clients/${clientId}/facebook/status`);
      setStatus(res.data);
      if (res.data.app_id) setAppId(res.data.app_id);
    } catch {
      setStatus({ has_app: false, connected: false, ad_accounts: [] });
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Pick up callback redirect status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("facebook") === "connected") {
      setSavedToast("Facebook connected. Auto-syncing current month's ad spend…");
      setTimeout(() => setSavedToast(null), 6000);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      load().then(() => {
        // Auto-fire spend sync once on successful connect
        const today = new Date();
        const m = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
        axios.post(`${API}/facebook/sync-spend/${clientId}/${m}`).then(r => {
          const d = r.data?.data || r.data || {};
          setSavedToast(`Connected & synced ${m}: ${(d.total_spend || 0).toLocaleString("en-IN")} ${d.currency || ""}`);
          setTimeout(() => setSavedToast(null), 6000);
        }).catch(() => {
          // sync failure is non-fatal — user can pick a different account / retry from the box
          setSavedToast("Connected. Pick an ad account, then click Sync.");
          setTimeout(() => setSavedToast(null), 5000);
        });
      });
    } else if (params.get("facebook_error")) {
      setError(`Facebook connection failed: ${params.get("facebook_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load, clientId]);

  const saveCreds = async () => {
    if (!appId.trim() || !appSecret.trim()) {
      setError("Both App ID and App Secret are required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await axios.put(`${API}/clients/${clientId}/facebook/app-creds`, {
        app_id: appId.trim(),
        app_secret: appSecret.trim(),
      });
      setEditing(false);
      setAppSecret("");
      setSavedToast("App credentials saved");
      setTimeout(() => setSavedToast(null), 3000);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const startOAuth = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/clients/${clientId}/facebook/auth-url`, {
        params: { origin: window.location.origin },
      });
      window.location.href = res.data.auth_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start Facebook auth");
      setLoading(false);
    }
  };

  const selectAdAccount = async (id) => {
    setLoading(true);
    try {
      await axios.put(`${API}/clients/${clientId}/facebook/select-account`, { ad_account_id: id });
      setSavedToast("Ad account linked");
      setTimeout(() => setSavedToast(null), 3000);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to select ad account");
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this client from Facebook? Token + ad accounts will be cleared (App ID/Secret kept).")) return;
    setLoading(true);
    try {
      await axios.post(`${API}/clients/${clientId}/facebook/disconnect`);
      setSavedToast("Disconnected");
      setTimeout(() => setSavedToast(null), 3000);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to disconnect");
    } finally {
      setLoading(false);
    }
  };

  if (!clientId) {
    return (
      <div className="form-group md:col-span-2 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="fb-connection-empty">
        <label className="form-label text-[#1877F2]">Facebook Ads Connection</label>
        <p className="text-xs text-[#71717A] mt-1">Save the client first, then connect Facebook from the edit page.</p>
      </div>
    );
  }

  const hasApp = !!status?.has_app;
  const connected = !!status?.connected;
  const expiresAt = status?.token_expires_at;
  const expiringSoon = expiresAt && (new Date(expiresAt) - new Date()) < 7 * 24 * 60 * 60 * 1000;

  return (
    <div className="form-group md:col-span-2 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="fb-connection-section">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <label className="form-label text-[#1877F2]">Facebook Ads Connection</label>
          <p className="text-xs text-[#71717A] mt-1">Each client uses its own Meta App. Enter this client&apos;s Meta App ID + Secret, then connect.</p>
        </div>
        {connected && !expiringSoon && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{background: "#22C55E22", color: "#22C55E"}}>● Connected</span>
        )}
        {connected && expiringSoon && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{background: "#F59E0B22", color: "#F59E0B"}}>● Expiring soon</span>
        )}
        {!connected && hasApp && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{background: "#71717A22", color: "#71717A"}}>App configured · not connected</span>
        )}
        {!hasApp && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{background: "#71717A22", color: "#71717A"}}>Not configured</span>
        )}
      </div>

      {savedToast && <div className="mb-3 p-2 text-xs rounded" style={{background: "#22C55E22", color: "#22C55E"}}>{savedToast}</div>}
      {error && <div className="mb-3 p-2 text-xs rounded" style={{background: "#FF444422", color: "#FF6666"}}>{error}</div>}

      {/* Step 1 — App credentials */}
      {(!hasApp || editing) && (
        <div className="space-y-3 p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded mb-3">
          <div className="text-xs text-[#71717A]">
            <span className="font-semibold text-[#1877F2]">Step 1 of 2 — Meta App credentials</span>
            <p className="mt-1">Get these from <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-[#FFEB00] hover:underline">developers.facebook.com/apps</a> → your app → Settings → Basic.</p>
          </div>
          <input
            type="text"
            className="input-default w-full text-sm"
            placeholder="Meta App ID (e.g. 945599844880142)"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            data-testid="fb-app-id-input"
          />
          <input
            type="password"
            className="input-default w-full text-sm"
            placeholder="Meta App Secret"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            data-testid="fb-app-secret-input"
          />
          <div className="flex gap-2">
            <button onClick={saveCreds} disabled={loading} className="btn-primary text-xs" data-testid="fb-save-creds-btn">
              {loading ? "Saving..." : "Save Credentials"}
            </button>
            {editing && (
              <button onClick={() => { setEditing(false); setAppSecret(""); setError(null); }} className="btn-secondary text-xs">Cancel</button>
            )}
          </div>
          <p className="text-[10px] text-[#52525B]">
            <strong>Whitelist this redirect URI</strong> in your Meta App → Facebook Login → Valid OAuth Redirect URIs:<br/>
            <code className="text-[#A1A1AA]">{`${window.location.origin}/api/facebook/callback`}</code>
            <button
              type="button"
              onClick={() => copyToClipboard(`${window.location.origin}/api/facebook/callback`, "Redirect URI copied")}
              className="ml-2 text-[#FFEB00] hover:underline"
              data-testid="fb-copy-redirect-uri-btn"
            >copy</button>
          </p>
        </div>
      )}

      {/* Troubleshoot panel (always available for per-client Meta apps) */}
      {hasApp && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => { setShowTroubleshoot(v => !v); if (!previewInfo) previewAuthUrl(); }}
            className="text-[10px] text-[#71717A] hover:text-[#FFEB00] underline"
            data-testid="fb-toggle-troubleshoot"
          >
            {showTroubleshoot ? "Hide" : "Show"} OAuth setup / troubleshoot
          </button>
          {showTroubleshoot && (
            <div className="mt-2 p-3 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-[11px] text-[#A1A1AA] space-y-2" data-testid="fb-troubleshoot-panel">
              <div className="font-semibold text-[#FFEB00]">Fix "Invalid Redirect URI" errors</div>
              <div>
                <div className="text-[#71717A]">1. Exact redirect URI to whitelist in Meta App:</div>
                <div className="flex items-start gap-2 mt-1">
                  <code className="text-[#E4E4E7] bg-[#18181B] px-2 py-1 rounded flex-1 break-all" data-testid="fb-exact-redirect-uri">
                    {previewInfo?.redirect_uri || `${window.location.origin}/api/facebook/callback`}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(previewInfo?.redirect_uri || `${window.location.origin}/api/facebook/callback`, "Redirect URI copied")}
                    className="text-[#FFEB00] hover:underline whitespace-nowrap"
                    data-testid="fb-copy-exact-uri-btn"
                  >copy</button>
                </div>
                {previewInfo?.resolved_via && (
                  <div className="text-[10px] text-[#52525B] mt-1">resolved via: {previewInfo.resolved_via}</div>
                )}
              </div>
              <div>
                <div className="text-[#71717A]">2. Paste it in Meta Developer Console at:</div>
                <div className="text-[10px]">App Dashboard → Products → <strong>Facebook Login for Business</strong> → Settings → <strong>Valid OAuth Redirect URIs</strong> (save changes).</div>
              </div>
              <div>
                <div className="text-[#71717A]">3. Common causes of "Invalid Redirect URI":</div>
                <ul className="list-disc pl-4 space-y-0.5 text-[10px]">
                  <li>Trailing slash mismatch (must be <strong>exactly</strong> what's shown above — no trailing /).</li>
                  <li>App is in <strong>Development mode</strong> AND the connecting Facebook user isn't listed as Admin / Developer / Tester on that app.</li>
                  <li>App doesn't have <strong>Facebook Login for Business</strong> product added (required for <code>ads_management</code>).</li>
                  <li>Whitelist was added but not <strong>Saved</strong> (Meta requires clicking "Save Changes" at the bottom).</li>
                  <li>App Secret changed in Meta but not updated here — click "Change credentials" above to re-enter.</li>
                </ul>
              </div>
              <div>
                <div className="text-[#71717A]">4. Full OAuth URL being sent to Meta (for diagnostics):</div>
                {previewInfo?.auth_url ? (
                  <div className="mt-1">
                    <textarea
                      readOnly
                      value={previewInfo.auth_url}
                      className="w-full text-[10px] bg-[#18181B] border border-[#2A2A2A] rounded p-2 text-[#E4E4E7] font-mono resize-none"
                      rows={3}
                      data-testid="fb-preview-auth-url"
                    />
                    <button
                      type="button"
                      onClick={() => copyToClipboard(previewInfo.auth_url, "Auth URL copied")}
                      className="text-[#FFEB00] hover:underline text-[10px] mt-1"
                      data-testid="fb-copy-auth-url-btn"
                    >copy full URL</button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={previewAuthUrl}
                    disabled={loading}
                    className="text-[10px] text-[#FFEB00] hover:underline"
                  >{loading ? "Loading…" : "Generate preview"}</button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2 — Connect / Reconnect */}
      {hasApp && !editing && (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs">
            <div>
              <div className="text-[#71717A]">Meta App ID</div>
              <div className="font-mono text-[#E4E4E7]" data-testid="fb-current-app-id">{status?.app_id}</div>
            </div>
            <button onClick={() => { setAppSecret(""); setEditing(true); }} className="text-[#FFEB00] hover:underline text-xs" data-testid="fb-edit-creds-btn">Change credentials</button>
          </div>

          {!connected && (
            <button
              onClick={startOAuth}
              disabled={loading}
              className="w-full py-2.5 rounded font-medium text-sm transition-colors"
              style={{background: "#1877F2", color: "#fff"}}
              data-testid="fb-connect-btn"
            >
              {loading ? "Redirecting…" : "Connect Facebook"}
            </button>
          )}

          {connected && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[#71717A]">Connected as</div>
                  <div className="text-[#E4E4E7]" data-testid="fb-connected-user">{status.user_name || "—"}</div>
                </div>
                <div>
                  <div className="text-[#71717A]">Token expires</div>
                  <div className={expiringSoon ? "text-[#F59E0B]" : "text-[#E4E4E7]"}>{formatExpiry(expiresAt)}</div>
                </div>
              </div>

              {/* Ad account picker */}
              <div>
                <label className="form-label text-xs">Ad Account for this client</label>
                <select
                  className="input-default w-full text-sm"
                  value={status.selected_ad_account_id || ""}
                  onChange={(e) => selectAdAccount(e.target.value)}
                  data-testid="fb-select-ad-account"
                >
                  <option value="">Select ad account…</option>
                  {(status.ad_accounts || []).map((a) => {
                    const id = a.id || a.account_id;
                    return (
                      <option key={id} value={id}>
                        {a.name || id} {a.business_name ? `· ${a.business_name}` : ""} {a.currency ? `(${a.currency})` : ""}
                      </option>
                    );
                  })}
                </select>
                <p className="text-[10px] text-[#71717A] mt-1">{(status.ad_accounts || []).length} ad account(s) available</p>
              </div>

              {/* Sync Spend */}
              {status.selected_ad_account_id && (
                <SyncSpendBox clientId={clientId} />
              )}

              <div className="flex gap-2">
                <button onClick={startOAuth} className="btn-secondary text-xs" data-testid="fb-reconnect-btn">Reconnect</button>
                <button onClick={disconnect} className="btn-secondary text-xs text-[#FF6666] border-[#FF444433]" data-testid="fb-disconnect-btn">Disconnect</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientFacebookConnection;
